import { useState } from "react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import { saveMoneyIn, deleteMoneyIn } from "../../api/expenses";
import { formatMoney } from "../../utils/money";
import { INPUT, Field } from "./ExpenseModal";

// A partner putting money into a company account, and (optionally) what it paid for.

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

export default function MoneyInModal({ entry, data, onClose }) {
  const [form, setForm] = useState(() => ({
    partner: entry?.partner || data.partners[0] || "",
    amount: entry ? (entry.amount_cents / 100).toFixed(2) : "",
    received_on: entry?.received_on ? String(entry.received_on).slice(0, 10) : today(),
    account_id: entry?.account_id || data.accounts.find((a) => a.owner === "company" && a.kind === "bank" && !a.archived)?.id || "",
    note: entry?.note || "",
    expense_ids: entry?.expense_ids || [],
  }));
  const [showPick, setShowPick] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // What it could have paid for: company-paid expenses, newest first.
  const companyIds = new Set(data.accounts.filter((a) => a.owner === "company").map((a) => a.id));
  const candidates = data.expenses.filter(
    (e) => e.status === "paid" && (form.expense_ids.includes(e.id) || !e.account_id || companyIds.has(e.account_id))
  );
  const picked = data.expenses.filter((e) => form.expense_ids.includes(e.id));
  const toggle = (id) =>
    setForm((f) => ({ ...f, expense_ids: f.expense_ids.includes(id) ? f.expense_ids.filter((x) => x !== id) : [...f.expense_ids, id] }));

  async function save() {
    setBusy(true);
    setError("");
    try {
      await saveMoneyIn(entry?.id, { ...form, account_id: form.account_id || null });
      onClose(true);
    } catch (e) {
      setError(e.message || "Couldn't save.");
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await deleteMoneyIn(entry.id);
      onClose(true);
    } catch (e) {
      setError(e.message || "Couldn't delete it.");
    }
  }

  const footer = (
    <div className="flex items-center justify-between gap-2">
      {entry ? (
        <Button variant="ghost" size="sm" onClick={remove} className="text-red-700">
          Delete
        </Button>
      ) : (
        <span />
      )}
      <Button onClick={save} disabled={busy} className="px-6">
        {busy ? "Saving…" : "Save"}
      </Button>
    </div>
  );

  return (
    <Modal isOpen title={entry ? "Money in" : "Add money in"} onClose={() => onClose(false)} footer={footer} maxWidthClass="max-w-lg">
      {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <div className="space-y-3">
        <p className="text-xs text-birdie-charcoal/55">
          When you or {data.partners.filter((p) => p !== data.partners[0]).join(" or ") || "a partner"} move personal money into a
          company account. Things paid on a personal card already count, so don't add those here.
        </p>
        <div>
          <span className="block text-xs font-semibold text-birdie-charcoal/70 mb-1">Who</span>
          <div className="flex gap-2">
            {data.partners.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setForm((f) => ({ ...f, partner: p }))}
                className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold ${
                  form.partner === p ? "bg-birdie-green text-white border-birdie-green" : "border-birdie-green/20 text-birdie-charcoal/75"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount">
            <input value={form.amount} onChange={set("amount")} inputMode="decimal" placeholder="0.00" className={`${INPUT} text-lg font-semibold`} />
          </Field>
          <Field label="Date">
            <input type="date" value={form.received_on} onChange={set("received_on")} className={INPUT} />
          </Field>
        </div>
        <Field label="Into">
          <select value={form.account_id} onChange={set("account_id")} className={INPUT}>
            <option value="">Not sure</option>
            {data.accounts
              .filter((a) => a.owner === "company" && (!a.archived || a.id === Number(form.account_id)))
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Note (optional)">
          <input value={form.note} onChange={set("note")} placeholder="To pay off the Chase card" className={INPUT} />
        </Field>
        <div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-birdie-charcoal/70">What it paid for (optional)</span>
            <button type="button" onClick={() => setShowPick((v) => !v)} className="text-xs font-semibold text-birdie-green">
              {showPick ? "Done" : "Choose"}
            </button>
          </div>
          {picked.length > 0 && !showPick && (
            <p className="text-xs text-birdie-charcoal/60 mt-1">
              {picked.map((e) => e.description).join(", ")} ·{" "}
              {formatMoney(picked.reduce((s, e) => s + Number(e.amount_cents), 0))}
            </p>
          )}
          {showPick && (
            <ul className="mt-2 max-h-60 overflow-y-auto divide-y divide-birdie-green/10 rounded-lg border border-birdie-green/10">
              {candidates.map((e) => (
                <li key={e.id}>
                  <label className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.expense_ids.includes(e.id)}
                      onChange={() => toggle(e.id)}
                      className="h-4 w-4 min-h-0 min-w-0 accent-birdie-green"
                    />
                    <span className="min-w-0 flex-1 truncate">{e.description}</span>
                    <span className="shrink-0 tabular-nums text-birdie-charcoal/60">{formatMoney(e.amount_cents)}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
